import {
  ArrowRight,
  FolderOpen,
  Highlighter,
  Link2,
  PanelsTopLeft,
  Radio,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import type { ComponentType, PropsWithChildren } from "react";
import { Link, type MetaFunction } from "react-router";

import { demoCreatorPortfolio } from "@/site/product/demo-portfolio";
import {
  getCreatorCollectionPath,
  getCreatorProfilePath,
} from "@/site/product/profile";
import { CollectionCover } from "@/site/ui/collection-cover";
import { HeroPreview } from "@/site/ui/hero-preview";
import { LogoLockup } from "@/site/ui/logo";
import { ThemeControl } from "@/theme/theme-control";
import { Button } from "@/ui/button";

export const meta: MetaFunction = () => {
  return [
    { title: "Highlighted FYI — your X work, highlighted" },
    {
      name: "description",
      content:
        "Turn your own X posts into a public creator profile with deliberate collections for guides, styles, artworks, and more.",
    },
  ];
};

// Introduce Highlighted FYI through a clearly labeled example portfolio and direct paths
// into both the studio and public profile.
//
export default function MarketingIndex() {
  const { collections, profile } = demoCreatorPortfolio;

  return (
    <div className="min-h-dvh overflow-hidden">
      <div
        aria-hidden="true"
        className="highlighted-grid pointer-events-none absolute inset-x-0 top-0 -z-20 h-[52rem] opacity-75 [mask-image:linear-gradient(to_bottom,black,transparent)]"
      />

      <header className="bg-background/85 sticky top-0 z-50 border-b backdrop-blur-xl">
        <div className="mx-auto flex h-17 w-[calc(100%-2rem)] max-w-7xl items-center justify-between sm:w-[calc(100%-3rem)]">
          <LogoLockup linkTo="/" />

          <nav
            aria-label="Main navigation"
            className="text-muted-foreground hidden items-center gap-7 text-sm md:flex"
          >
            <Link
              className="hover:text-foreground transition-colors"
              to="/pricing"
            >
              Pricing
            </Link>
            <a
              className="hover:text-foreground transition-colors"
              href="#collections"
            >
              Creator collections
            </a>
            <a className="hover:text-foreground transition-colors" href="#how">
              How it works
            </a>
            <a
              className="hover:text-foreground transition-colors"
              href="#principle"
            >
              The principle
            </a>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <ThemeControl />
            <Button asChild className="rounded-full px-4" size="sm">
              <Link to="/studio">
                Open studio
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto grid w-[calc(100%-2rem)] max-w-7xl gap-14 pt-16 pb-20 sm:w-[calc(100%-3rem)] sm:pt-24 sm:pb-28 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:gap-20 lg:pt-28">
          <div className="marketing-enter">
            <p className="border-signal/35 bg-signal/10 text-signal-strong inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold tracking-[0.08em] uppercase">
              <Radio className="size-3.5" />A portfolio for your work on X
            </p>
            <h1 className="font-display mt-7 max-w-2xl text-[3.6rem] leading-[0.92] font-semibold tracking-[-0.065em] text-balance sm:text-7xl lg:text-[5.4rem]">
              Your X work, highlighted.
            </h1>
            <p className="text-muted-foreground mt-7 max-w-xl text-lg leading-8 sm:text-xl">
              Turn your own X posts into a public portfolio with collections for
              guides, styles, artworks, and every other part of your practice.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                className="h-12 rounded-full px-6 text-base"
                size="lg"
              >
                <Link to="/studio">
                  Build your portfolio
                  <ArrowRight />
                </Link>
              </Button>
              <Button
                asChild
                className="h-12 rounded-full px-6 text-base"
                size="lg"
                variant="outline"
              >
                <Link to={getCreatorProfilePath(profile)}>
                  Explore the example
                </Link>
              </Button>
            </div>
            <div className="text-muted-foreground mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              {publicEnv.stripeTrialDays > 0 && (
                <span>{publicEnv.stripeTrialDays}-day free trial</span>
              )}
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="text-success size-4" />X login verifies
                your handle
              </span>
              <span>Original posts stay on X</span>
            </div>
          </div>

          <div className="marketing-enter marketing-enter-delay">
            <HeroPreview />
          </div>
        </section>

        <section className="border-y">
          <div className="text-muted-foreground mx-auto grid w-[calc(100%-2rem)] max-w-7xl grid-cols-1 gap-5 py-5 text-sm sm:w-[calc(100%-3rem)] sm:grid-cols-3 sm:gap-8">
            <PromiseLine icon={UserRound}>Lead with who you are</PromiseLine>
            <PromiseLine icon={FolderOpen}>Organize what you make</PromiseLine>
            <PromiseLine icon={PanelsTopLeft}>
              Publish it in your order
            </PromiseLine>
          </div>
        </section>

        <section
          className="mx-auto w-[calc(100%-2rem)] max-w-7xl py-24 sm:w-[calc(100%-3rem)] sm:py-32"
          id="collections"
        >
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p className="text-signal-strong text-xs font-semibold tracking-[0.14em] uppercase">
                Beyond one Highlights tab
              </p>
              <h2 className="font-display mt-4 max-w-2xl text-4xl leading-[1.02] font-semibold tracking-[-0.05em] sm:text-6xl">
                Let people browse your practice, not your posting history.
              </h2>
            </div>
            <p className="text-muted-foreground max-w-md leading-7">
              X can feature selected posts. Highlighted FYI gives each body of
              work a name, a sequence, and the layout it needs.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {collections.map((collection) => (
              <CollectionCover
                key={collection.id}
                collection={collection}
                to={getCreatorCollectionPath(profile, collection)}
              />
            ))}
          </div>
        </section>

        <section
          className="bg-ink text-ink-foreground relative overflow-hidden"
          id="how"
        >
          <div
            aria-hidden="true"
            className="highlighted-night-grid pointer-events-none absolute inset-0 opacity-45"
          />
          <div className="relative mx-auto w-[calc(100%-2rem)] max-w-7xl py-24 sm:w-[calc(100%-3rem)] sm:py-32">
            <p className="text-signal text-xs font-semibold tracking-[0.14em] uppercase">
              Three small moves
            </p>
            <h2 className="font-display mt-4 max-w-3xl text-4xl leading-[1.02] font-semibold tracking-[-0.05em] sm:text-6xl">
              From one Highlights tab to a body of work.
            </h2>

            <div className="mt-14 grid border-y border-white/15 md:grid-cols-3">
              <ProcessStep
                icon={UserRound}
                number="01"
                title="Connect your identity"
              >
                Sign in with X so your verified handle anchors every collection
                and gives visitors a clear way back to you on X.
              </ProcessStep>
              <ProcessStep icon={Link2} number="02" title="Add your posts">
                Paste one or many X post links and choose their collections. Add
                optional context about your role, process, or results.
              </ProcessStep>
              <ProcessStep
                icon={FolderOpen}
                number="03"
                title="Shape public collections"
              >
                Arrange posts as a Feed or Grid. Start as a draft, then publish
                when you’re ready to share.
              </ProcessStep>
            </div>
          </div>
        </section>

        <section
          className="mx-auto grid w-[calc(100%-2rem)] max-w-7xl gap-12 py-24 sm:w-[calc(100%-3rem)] sm:py-32 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:gap-24"
          id="principle"
        >
          <div className="signal-rings relative mx-auto flex aspect-square w-full max-w-sm items-center justify-center">
            <div className="bg-card flex size-36 flex-col items-center justify-center rounded-full border text-center shadow-xl">
              <Highlighter className="text-signal-strong size-7" />
              <span className="font-display mt-2 text-lg font-semibold">
                yours
              </span>
              <span className="text-muted-foreground text-xs">
                by authorship
              </span>
            </div>
          </div>

          <div>
            <p className="text-signal-strong text-xs font-semibold tracking-[0.14em] uppercase">
              The Highlighted FYI principle
            </p>
            <h2 className="font-display mt-4 text-4xl leading-[1.02] font-semibold tracking-[-0.05em] sm:text-6xl">
              The post stays on X. The body of work lives here.
            </h2>
            <p className="text-muted-foreground mt-7 max-w-2xl text-lg leading-8">
              Bookmarks are for what you save. Highlighted FYI is a public index
              of what you made: selected, titled, and arranged by you, while the
              original post remains on X. Embeds may shorten long posts;
              visitors can open the original to read more.
            </p>
            <Button
              asChild
              className="mt-9 h-12 rounded-full px-6 text-base"
              size="lg"
            >
              <Link to="/studio">
                Make your first collection
                <ArrowRight />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t">
        <div className="text-muted-foreground mx-auto flex w-[calc(100%-2rem)] max-w-7xl flex-col gap-5 py-8 text-sm sm:w-[calc(100%-3rem)] sm:flex-row sm:items-center sm:justify-between">
          <LogoLockup className="text-foreground" linkTo="/" />
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2">
            <Link
              className="hover:text-foreground transition-colors"
              to="/pricing"
            >
              Pricing
            </Link>
            <Link
              className="hover:text-foreground transition-colors"
              to="/terms"
            >
              Terms
            </Link>
            <Link
              className="hover:text-foreground transition-colors"
              to="/privacy"
            >
              Privacy
            </Link>
          </nav>
          <p>© {new Date().getFullYear()} Highlighted FYI</p>
        </div>
      </footer>
    </div>
  );
}

interface PromiseLineProps {
  icon: ComponentType<{ className?: string }>;
}

// Keep the three creator promises scannable between the hero and examples.
//
function PromiseLine({
  children,
  icon: Icon,
}: PropsWithChildren<PromiseLineProps>) {
  return (
    <span className="flex items-center justify-center gap-2 sm:justify-start">
      <Icon className="text-signal-strong size-4" />
      {children}
    </span>
  );
}

interface ProcessStepProps {
  icon: ComponentType<{ className?: string }>;
  number: string;
  title: string;
}

// Explain one stage of creator curation without turning it into a checklist.
//
function ProcessStep({
  children,
  icon: Icon,
  number,
  title,
}: PropsWithChildren<ProcessStepProps>) {
  return (
    <article className="border-white/15 py-8 md:border-r md:px-8 md:py-10 md:first:pl-0 md:last:border-r-0 md:last:pr-0">
      <div className="flex items-center justify-between">
        <span className="flex size-11 items-center justify-center rounded-2xl bg-white/8">
          <Icon className="text-signal size-5" />
        </span>
        <span className="font-display text-2xl text-white/55">{number}</span>
      </div>
      <h3 className="font-display mt-8 text-2xl font-semibold">{title}</h3>
      <p className="mt-3 leading-7 text-white/55">{children}</p>
    </article>
  );
}
